import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { statisticsApi } from '@/api/statistics'
import type { ChartType } from '@/types/statistics'

const CHART_TABS: { type: ChartType; label: string; shortLabel: string }[] = [
  { type: 'WORDS_PER_DAY', label: 'Words per day', shortLabel: 'Words' },
  { type: 'ACCURACY', label: 'Accuracy', shortLabel: 'Accuracy' },
  { type: 'STUDY_TIME', label: 'Study time', shortLabel: 'Study time' },
  { type: 'REVIEW_SUCCESS', label: 'Review success', shortLabel: 'Review' },
]

const DAY_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
]

const CHART_META: Record<
  ChartType,
  { unit: string; color: string; useBar: boolean; yLabel: string }
> = {
  WORDS_PER_DAY: { unit: 'words', color: '#6366f1', useBar: true, yLabel: 'Words' },
  ACCURACY: { unit: '%', color: '#10b981', useBar: false, yLabel: 'Accuracy %' },
  STUDY_TIME: { unit: 'min', color: '#f59e0b', useBar: true, yLabel: 'Minutes' },
  REVIEW_SUCCESS: { unit: '%', color: '#3b82f6', useBar: false, yLabel: 'Success %' },
}

function formatLabel(dateStr: string, days: number): string {
  const date = new Date(dateStr)
  if (days <= 7) {
    return date.toLocaleDateString('en', { weekday: 'short' })
  }
  return date.toLocaleDateString('en', { month: 'short', day: 'numeric' })
}

export function ProgressChart() {
  const [activeType, setActiveType] = useState<ChartType>('WORDS_PER_DAY')
  const [days, setDays] = useState(30)

  const { data, isLoading } = useQuery({
    queryKey: ['statistics', 'chart', activeType, days],
    queryFn: () => statisticsApi.getChartData(activeType, days),
  })

  const meta = CHART_META[activeType]

  const chartData = data
    ? data.labels.map((label, i) => ({
        date: formatLabel(label, days),
        value: data.values[i],
      }))
    : []

  const tickInterval = days <= 7 ? 0 : days <= 30 ? 4 : 13

  return (
    <div className="space-y-4">
      {/* Chart type tabs — 2×2 on mobile so all four fit; row on sm+ */}
      <div
        className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap sm:gap-1"
        data-testid="progress-chart-tabs"
      >
        {CHART_TABS.map((tab) => (
          <button
            key={tab.type}
            type="button"
            onClick={() => setActiveType(tab.type)}
            className={`min-h-9 rounded-md px-2 py-1.5 text-xs font-medium transition-colors sm:shrink-0 sm:px-3 sm:text-sm ${
              activeType === tab.type
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <span className="sm:hidden">{tab.shortLabel}</span>
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Day range selector */}
      <div className="flex gap-1">
        {DAY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setDays(opt.value)}
            className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
              days === opt.value
                ? 'bg-secondary text-secondary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="h-52">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : meta.useBar ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                interval={tickInterval}
                stroke="hsl(var(--muted-foreground))"
              />
              <YAxis
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--muted-foreground))"
                label={{
                  value: meta.yLabel,
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' },
                }}
              />
              <Tooltip
                formatter={(v: number) => [`${v} ${meta.unit}`, meta.yLabel]}
                contentStyle={{
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
              <Bar dataKey="value" fill={meta.color} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                interval={tickInterval}
                stroke="hsl(var(--muted-foreground))"
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--muted-foreground))"
                label={{
                  value: meta.yLabel,
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' },
                }}
              />
              <Tooltip
                formatter={(v: number) => [`${v}${meta.unit}`, meta.yLabel]}
                contentStyle={{
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={meta.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
